import React, { memo } from 'react';
import { useUnistyles, StyleSheet } from 'react-native-unistyles';
import { Text, View, TouchableOpacity, Linking } from 'react-native';
import FontAwesome5 from "@react-native-vector-icons/fontawesome5";
import FileViewer from "react-native-file-viewer";
import { getFile } from '@/utils/FileManager';
import Toast from 'react-native-toast-message'
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { archiveSong, deleteSong } from '@/data/songs';
import { showAlert } from '@/utils/showAlert';
import { useRouter } from 'expo-router';
import DropdownMenu from '../General/DropdownMenu';
import Unicon from '../General/Unicon';

const SongCard = ({song}) => {
    const {theme: {colors}} = useUnistyles();
    const router = useRouter();

    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser()

    const deleteSongMutation = useMutation({
        mutationFn: deleteSong,
        onSuccess: () => {
            queryClient.setQueryData(QueryKeys.songs.all(), old => old.filter(s => s.id != song.id));
        },
    })

    const archiveSongMutation = useMutation({
        mutationFn: archiveSong,
        onSuccess: () => {
            queryClient.setQueryData(QueryKeys.songs.all(), old => old.map(s => s.id == song.id ? {...s, isArchived: song.isArchived} : s));
        },
    })


    const openFile = async (fileKey) => {
        const file = await getFile(fileKey)

        if(file){
            FileViewer.open(file);
        }
        else{
            Toast.show({
                type: 'error',
                text2: `Failed to open file. It may be corrupted or missing`
            });
        }
    }

    const options = [];
    loggedInUser?.can?.("Update Songs") && options.push({name: "Edit Song", onSelect: () => router.push({pathname: "/music/create" + song?.id})})
    loggedInUser?.can?.("Update Songs") && (!song?.isArchived ? options.push({name: "Archive Song", onSelect: () => archiveSongMutation.mutate({id: song?.id, isArchived: true})}) : options.push({name: "Unarchive Song", onSelect: () => archiveSongMutation.mutate({id: song?.id, isArchived: false})}))
    loggedInUser?.can?.("Delete Songs") && options.push({name: "Delete Song", onSelect: () => {
        showAlert({
            title: "Delete Song",
            subtitle: "Are you sure you want to delete this song? Deleting a song will remove it from any events it was included in and will not be recoverable.",
            onConfirm: () => {
                deleteSongMutation.mutate(song?.id);
            },
            confirmText: "Delete"
        })
    }})

    return (
        <View style={styles.cardContainer}>
            <View style={styles.topRow}>
                <View>
                    <Text style={styles.songName}>{song.name}</Text>
                    <Text style={styles.artistName}>{song.artist}</Text>
                    <Text style={styles.songDetails}>{song.key || song.defaultKey} • {song.tempo} bpm</Text>
                </View>
                <View>
                    { options.length > 0 && <DropdownMenu options={options} />}
                </View>
            </View>

            <View style={styles.bottomRow}>
                <View style={styles.resourcesContainer}>
                    { song.chordFileID && <TouchableOpacity onPress={() => openFile(song.chordFile.key)}><Text style={styles.linkText}>Chords</Text></TouchableOpacity> }
                    { song.lyricFileID && <TouchableOpacity onPress={() => openFile(song.lyricFile.key)}><Text style={styles.linkText}>Lyrics</Text></TouchableOpacity> }
                    <TouchableOpacity onPress={() => router.push({pathname: "/library/folders/" + song.folderID, params: {path: "music/" + song.folderID + "/"}})}><Text style={styles.linkText}>All Resources</Text></TouchableOpacity>
                </View>

                <View style={styles.linksContainer}>
                    { song.selfHostedTrackFileID && <TouchableOpacity onPress={() => openFile(song.selfHostedTrackFile.key)}style={styles.iconButton}><Unicon name="play" size={24} colorVariant="text" /></TouchableOpacity> }
                    { song.appleMusicURL && <TouchableOpacity onPress={() => Linking.openURL(song.appleMusicURL)} style={styles.iconButton}><Unicon name="logo-apple" size={24} colorVariant="text" /></TouchableOpacity> }
                    { song.spotifyURL && <TouchableOpacity onPress={() => Linking.openURL(song.spotifyURL)}style={styles.iconButton}><Unicon library="fa5" name="spotify" size={24} colorVariant="text" /></TouchableOpacity> }
                    { song.amazonMusicURL && <TouchableOpacity onPress={() => Linking.openURL(song.amazonMusicURL)} style={styles.iconButton} ><Unicon name="logo-amazon" size={24} colorVariant="text" /></TouchableOpacity> }
                    { song.youtubeMusicURL && <TouchableOpacity onPress={() => Linking.openURL(song.youtubeMusicURL)} style={styles.iconButton} ><Unicon name="logo-youtube" size={24} colorVariant="text" /></TouchableOpacity> }
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create(({colors, fonts}) => ({
    cardContainer: {
        backgroundColor:colors.surface, 
        borderRadius:8, 
        marginVertical:8, 
        paddingHorizontal:15, 
        paddingVertical:10
    },
    topRow: {
        flexDirection:"row", 
        justifyContent:"space-between"
    },
    songName: {
        color: colors.text,
        fontSize:15,
        fontFamily: fonts.bold
    },
    artistName: {
        fontSize:12, 
        fontStyle:"italic", 
        color:colors.textSecondary, 
        marginTop:3
    },
    songDetails: {
        fontSize:12, 
        fontStyle:"italic", 
        color:colors.textSecondary, 
        marginTop:3
    },
    bottomRow: {
        flexDirection:"row", 
        justifyContent:"space-between", 
        alignItems:"center", 
        paddingTop:10
    },
    resourcesContainer: {
        flexDirection:"row", 
        justifyContent:"flex-start", 
        alignItems:"center", 
        gap:10
    },
    linkText: {
        color: colors.primary, 
        fontSize:14,
        fontFamily: fonts.heading
    },
    linksContainer: {
        flexDirection:"row", 
        justifyContent:"flex-end", 
        alignItems:"center"
    },
    iconButton: {
        marginLeft:15
    }
}))

export default memo(SongCard)